// src/components/FilterBar.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './FilterBar.css';
import ViewportSelect from './ViewportSelect';

// Puedes cambiar estas categorías por las que vengan de tu base de datos o API
const CATEGORIAS_DISPONIBLES = [
  "Oposiciones", "Idiomas", "Informática", "Sanidad", "Administración", "Seguridad"
];

const FilterBar = ({ onFilterChange, isCompact = false }) => {
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sort, setSort] = useState('recent');
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  // Cada vez que cambia un estado, avisamos al padre (CoursesPage)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFilterChange({ search, tags: selectedTags, sort });
    }, 300); // Pequeño debounce para la búsqueda
    return () => clearTimeout(timeoutId);
  }, [search, selectedTags, sort, onFilterChange]);

  useEffect(() => {
    if (!isMobileModalOpen) return undefined;

    document.body.classList.add('mobile-filters-scroll-lock');
    return () => {
      document.body.classList.remove('mobile-filters-scroll-lock');
    };
  }, [isMobileModalOpen]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSort('recent');
    setIsMobileModalOpen(false);
  };

  const activeFiltersCount = selectedTags.length + (sort !== 'recent' ? 1 : 0);

  return (
    <div className={`filter-bar-container ${isCompact ? 'is-compact' : ''}`}>
      
      {/* 🔹 BARRA PRINCIPAL (Búsqueda + Botón Filtros Móvil) */}
      <div className="filter-main-row">
        <div className="search-input-wrapper">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input 
            type="text" 
            placeholder="¿Qué quieres aprender hoy?" 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Botón que solo se ve en móvil para abrir el modal */}
        <button 
          className="btn-mobile-filters" 
          onClick={() => setIsMobileModalOpen(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
          </svg>
          <span className="btn-text">Filtros</span>
          {activeFiltersCount > 0 && (
            <span className="filter-badge">{activeFiltersCount}</span>
          )}
        </button>
      </div>

      {/* 🔹 CONTROLES DE ESCRITORIO (Se ocultan en móvil) */}
      <div className="desktop-filters">
        <div className="tags-scroll-container">
          {CATEGORIAS_DISPONIBLES.map(tag => (
            <button
              key={tag}
              className={`tag-chip ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="sort-wrapper">
          <ViewportSelect value={sort} onChange={(e) => setSort(e.target.value)} className="sort-select" aria-label="Ordenar cursos">
            <option value="recent">Más recientes</option>
            <option value="price_asc">Precio: Menor a Mayor</option>
            <option value="price_desc">Precio: Mayor a Menor</option>
          </ViewportSelect>
        </div>
      </div>

      {/* 🔹 BOTTOM SHEET (Modal para Móviles) */}
      <div className={`mobile-filters-overlay ${isMobileModalOpen ? 'open' : ''}`} onClick={() => setIsMobileModalOpen(false)}>
        <div className="mobile-filters-sheet" onClick={(e) => e.stopPropagation()}>
          
          <div className="sheet-header">
            <h3>Filtros y Orden</h3>
            <button className="btn-close-sheet" onClick={() => setIsMobileModalOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="sheet-content">
            {/* Ordenar en Móvil */}
            <div className="filter-section">
              <h4>Ordenar por</h4>
              <div className="sort-options-mobile">
                {[
                  { id: 'recent', label: 'Más recientes' },
                  { id: 'price_asc', label: 'Precio: Menor a Mayor' },
                  { id: 'price_desc', label: 'Precio: Mayor a Menor' }
                ].map(option => (
                  <label key={option.id} className={`sort-radio-card ${sort === option.id ? 'active' : ''}`}>
                    <input 
                      type="radio" 
                      name="sortMobile" 
                      value={option.id} 
                      checked={sort === option.id}
                      onChange={(e) => setSort(e.target.value)}
                    />
                    {option.label}
                    {sort === option.id && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                  </label>
                ))}
              </div>
            </div>

            {/* Categorías en Móvil */}
            <div className="filter-section">
              <h4>Categorías</h4>
              <div className="tags-grid-mobile">
                {CATEGORIAS_DISPONIBLES.map(tag => (
                  <button
                    key={tag}
                    className={`tag-chip ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sheet-footer">
            <button className="btn-clear" onClick={clearFilters}>Limpiar</button>
            <button className="btn-apply" onClick={() => setIsMobileModalOpen(false)}>
              Ver resultados
            </button>
          </div>
          
        </div>
      </div>

    </div>
  );
};

FilterBar.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  isCompact: PropTypes.bool,
};

export default FilterBar;